import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "src/services/apiClient";
import { activeRows, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";
import { allowedFactorRange, type GoaCode } from "src/helpers/goaRules";
import { insertTemplate } from "src/helpers/invoiceTemplates";
import type { ServicesByDay } from "./servicesRules";

// Story 16b (Plus and above): suggestions from the practice's own invoices (nightly GoaUsageStats) or,
// without enough history yet, from its templates — POST /invoice-suggestions. Runs from step 2 on:
// step 2 shows diagnoses often entered together (`relatedDiagnoses`), step 3 the codes that go with the
// diagnoses (`isDiagnosisSuggestion`), step 4 everything.

export type Suggestion = {
    id: string;
    kind: "add" | "remove" | "factor";
    date: string;
    code: string;
    factor: number | null;
    reason: { key: string; params: Record<string, string | number> };
};
export type SuggestionBasis = "doctor" | "praxis" | "templates" | "none";
export type RelatedDiagnosis = { text: string; reason: { key: string; params: Record<string, string | number> } };

export type Suggestions =
    | { status: "off" }
    | { status: "loading" }
    | { status: "error" }
    | { status: "done"; suggestions: Suggestion[]; relatedDiagnoses: RelatedDiagnosis[]; basis: SuggestionBasis; invoiceCount: number };

/** A code that usually goes with an entered diagnosis. */
export const isDiagnosisSuggestion = (s: Suggestion) => s.reason.key.startsWith("diagnosis.");

/** Typing a code changes the lines on every keystroke — ask once the typing pauses. */
const DEBOUNCE_MS = 400;

/** Runs whenever `enabled` and the lines, diagnoses or owner change; `off` when not enabled. */
export function useInvoiceSuggestions(
    services: ServicesByDay,
    days: string[],
    diagnoses: string[],
    ownerPick: string | undefined,
    enabled: boolean,
): Suggestions {
    const body = useMemo(
        () => JSON.stringify({
            ...(ownerPick ? { doctorId: ownerPick } : {}),
            diagnoses,
            treatmentDates: days,
            services: days.flatMap((date) => activeRows(services[date] ?? []).filter((r) => r.code.trim()).map((r) => ({ date, code: r.code.trim(), factor: r.factor }))),
        }),
        [services, days, diagnoses, ownerPick],
    );
    const [state, setState] = useState<{ body: string; result: Suggestions } | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        const timer = window.setTimeout(() => {
            apiRequest<{ suggestions: Suggestion[]; relatedDiagnoses?: RelatedDiagnosis[]; basis: SuggestionBasis; invoiceCount: number }>(
                "/invoice-suggestions",
                { method: "POST", body },
                { "Content-Type": "application/json" },
            )
                .then((res) => !cancelled && setState({ body, result: { status: "done", ...res, relatedDiagnoses: res.relatedDiagnoses ?? [] } }))
                .catch(() => !cancelled && setState({ body, result: { status: "error" } }));
        }, DEBOUNCE_MS);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [enabled, body]);

    if (!enabled) return { status: "off" };
    // The last answer stays until the next one is in: step 3 shows this list above the lines, and
    // dropping it on every change made the lines jump. Suggestions never block anything, so a
    // moment of outdated ones does no harm.
    return state ? state.result : { status: "loading" };
}

/** The services after taking a suggestion. Added lines get the catalog's description and Einfachsatz,
 *  and the usual factor (else the section's standard factor); they are checked like any other line. */
export function applySuggestion(services: ServicesByDay, s: Suggestion, catalogByNr: Map<string, GoaCode>): ServicesByDay {
    const rows = services[s.date] ?? [];
    let next: GoaLineDraft[];
    if (s.kind === "add") {
        const entry = catalogByNr.get(s.code);
        next = insertTemplate(rows, {
            lines: [{
                code: s.code,
                description: entry?.description ?? "",
                quantity: 1,
                price: entry?.euroAmount ?? 0,
                factor: s.factor ?? allowedFactorRange(s.code, catalogByNr).default,
                justification: "",
                materialLines: [],
            }],
        });
    } else if (s.kind === "remove") {
        next = rows.flatMap((r) => (r.code.trim() !== s.code ? [r] : r._status === "new" ? [] : [{ ...r, _status: "deleted" as const }]));
    } else {
        next = rows.map((r) => (r.code.trim() === s.code && r._status !== "deleted" && s.factor ? { ...r, factor: s.factor } : r));
    }
    return { ...services, [s.date]: next };
}
